// Staff management page — super_admin only. Create, edit role, activate/deactivate, delete.
import { useEffect, useMemo, useState } from "react";
import { Plus, ShieldCheck, ShieldOff, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError, useAdminAuth } from "@/lib/adminAuth";

const ROLE_LABELS = {
    super_admin: "Super Admin",
    reservations_manager: "Reservations Manager",
    content_manager: "Content Manager",
};

function Modal({ open, onClose, children }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 bg-[#0b1f22]/70 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl border border-[#0b1f22]/10 max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-[#0b1f22]/40 hover:text-[#0b1f22]"><X size={18} /></button>
                {children}
            </div>
        </div>
    );
}

function AddStaffModal({ open, onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "reservations_manager" });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await adminApi.post("/admin/staff", {
                ...form,
                email: form.email.trim().toLowerCase(),
            });
            toast.success(`${data.name} added — temporary password shared.`);
            onCreated(data);
            onClose();
            setForm({ name: "", email: "", password: "", role: "reservations_manager" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Modal open={open} onClose={onClose}>
            <h2 className="font-display text-2xl text-[#0b1f22]">Add staff member</h2>
            <p className="text-xs text-[#0b1f22]/55 mt-1">They'll be prompted to change the temporary password on first login.</p>
            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="add-staff-form">
                <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Full name</span>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="mt-1 w-full border border-[#0b1f22]/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold" />
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Email</span>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="mt-1 w-full border border-[#0b1f22]/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold" />
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Temporary password (8+ chars)</span>
                    <input required minLength={8} type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="mt-1 w-full border border-[#0b1f22]/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold" />
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Role</span>
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                        className="mt-1 w-full border border-[#0b1f22]/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold bg-white">
                        <option value="reservations_manager">{ROLE_LABELS.reservations_manager}</option>
                        <option value="content_manager">{ROLE_LABELS.content_manager}</option>
                        <option value="super_admin">{ROLE_LABELS.super_admin}</option>
                    </select>
                </label>
                <button type="submit" disabled={busy} className="w-full bg-[#0b1f22] text-ivory px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] disabled:opacity-60 hover:bg-[#0b1f22]/90">
                    {busy ? "Adding…" : "Add staff member"}
                </button>
            </form>
        </Modal>
    );
}

export default function AdminStaff() {
    const { user } = useAdminAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/admin/staff");
            setRows(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const update = async (id, patch) => {
        try {
            const { data } = await adminApi.patch(`/admin/staff/${id}`, patch);
            setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
            toast.success("Updated");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id, email) => {
        if (!window.confirm(`Remove ${email}? This can't be undone.`)) return;
        try {
            await adminApi.delete(`/admin/staff/${id}`);
            setRows((rs) => rs.filter((r) => r.id !== id));
            toast.success("Staff removed");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div>
            <header className="mb-8 flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Access · Super Admin</div>
                    <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] mt-3">Staff accounts</h1>
                    <p className="text-[#0b1f22]/60 mt-2 text-sm">Add, disable, or change roles for anyone who signs into the admin dashboard.</p>
                </div>
                <button onClick={() => setAddOpen(true)} data-testid="add-staff-btn" className="bg-gold text-navy px-5 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-gold/90 inline-flex items-center gap-2">
                    <Plus size={13} /> Add staff
                </button>
            </header>

            {loading ? (
                <div className="text-sm text-[#0b1f22]/50">Loading staff…</div>
            ) : (
                <div className="bg-white rounded-2xl border border-[#0b1f22]/10 overflow-hidden">
                    <table className="w-full text-sm" data-testid="staff-table">
                        <thead className="bg-[#0b1f22]/[0.04] text-[10px] uppercase tracking-[0.2em] text-[#0b1f22]/60">
                            <tr>
                                <th className="text-left px-5 py-3">Name</th>
                                <th className="text-left px-5 py-3">Email</th>
                                <th className="text-left px-5 py-3">Role</th>
                                <th className="text-left px-5 py-3">Status</th>
                                <th className="text-right px-5 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const isMe = r.id === user?.id;
                                return (
                                    <tr key={r.id} className="border-t border-[#0b1f22]/5">
                                        <td className="px-5 py-4">{r.name} {isMe && <span className="text-[10px] uppercase tracking-[0.18em] text-gold ml-2">You</span>}</td>
                                        <td className="px-5 py-4 text-[#0b1f22]/75">{r.email}</td>
                                        <td className="px-5 py-4">
                                            {isMe ? (
                                                <span className="text-[#0b1f22]/70">{ROLE_LABELS[r.role]}</span>
                                            ) : (
                                                <select value={r.role} onChange={(e) => update(r.id, { role: e.target.value })}
                                                    className="border border-[#0b1f22]/10 rounded-lg px-2 py-1 text-sm bg-white">
                                                    <option value="reservations_manager">{ROLE_LABELS.reservations_manager}</option>
                                                    <option value="content_manager">{ROLE_LABELS.content_manager}</option>
                                                    <option value="super_admin">{ROLE_LABELS.super_admin}</option>
                                                </select>
                                            )}
                                        </td>
                                        <td className="px-5 py-4">
                                            {r.active ? (
                                                <span className="inline-flex items-center gap-1 text-[11px] text-jade font-medium"><ShieldCheck size={12} /> Active</span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[11px] text-[#0b1f22]/50"><ShieldOff size={12} /> Disabled</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-4 text-right">
                                            {!isMe && (
                                                <div className="inline-flex items-center gap-2">
                                                    <button onClick={() => update(r.id, { active: !r.active })} className="text-[10px] uppercase tracking-[0.2em] text-[#0b1f22]/60 hover:text-gold">
                                                        {r.active ? "Disable" : "Enable"}
                                                    </button>
                                                    <button onClick={() => remove(r.id, r.email)} className="text-red-500/80 hover:text-red-600"><Trash2 size={14} /></button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <AddStaffModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={(row) => setRows((rs) => [...rs, row])} />
        </div>
    );
}
